import { Reducer, useCallback, useMemo, useReducer, useState } from 'react';
import { Tag } from 'tags/models/tags';
import { v4 as uuidv4 } from 'uuid';

import { Option } from 'common/components/Inputs/Autocomplete/utils/models';
import { useDebounce } from 'common/useDebounce';
import { validateNumberOfAssignedTags, validateTagLength } from 'tags/utils/validators';
import { useTranslation } from 'react-i18next';
import { GetElementType } from 'src/common/tsHelpers';
import { useInfinityTagsQuery } from './useInfinityTagsQuery';

const mapTagsToOption = (tags: Tag[]) => {
  return tags.map((tag) => ({
    id: tag.id.toString(),
    name: tag.name,
  }));
};

type TagOption = GetElementType<ReturnType<typeof mapTagsToOption>>;

const ACTION_TYPES = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  RESET: 'RESET',
} as const;

type Actions =
  | { type: typeof ACTION_TYPES.CREATE; payload: string }
  | {
      type: typeof ACTION_TYPES.UPDATE;
      payload: TagOption[];
    }
  | { type: typeof ACTION_TYPES.RESET };

type State = {
  dirty: boolean;
  selectedTagsOptions: TagOption[];
  createdTags: TagOption[];
  initialTagsIds: Tag[];
};

const reducer: Reducer<State, Actions> = (state, action) => {
  if (action.type === ACTION_TYPES.CREATE) {
    const newId = uuidv4();
    const createdTag = { id: newId, name: action.payload };
    return {
      ...state,
      dirty: true,
      createdTags: [...state.createdTags, createdTag],
      selectedTagsOptions: [...state.selectedTagsOptions, createdTag],
    };
  }

  if (action.type === ACTION_TYPES.UPDATE) {
    return {
      ...state,
      dirty: true,
      selectedTagsOptions: action.payload,
    };
  }

  if (action.type === ACTION_TYPES.RESET) {
    return {
      ...state,
      dirty: false,
      createdTags: [],
      selectedTagsOptions: mapTagsToOption(state.initialTagsIds),
    };
  }

  return state;
};

type UseTagsAutocopleteInputProps = {
  initialAssignedTags: Tag[];
};

export const useTagsAutocompleteInput = ({ initialAssignedTags }: UseTagsAutocopleteInputProps) => {
  const { t } = useTranslation();
  const [searchTagsQuery, setSearchTagsQuery] = useState('');

  const debouncedSearchQuery = useDebounce(searchTagsQuery, 500);
  const [state, dispatch] = useReducer(reducer, {
    dirty: false,
    initialTagsIds: initialAssignedTags,
    createdTags: [] as TagOption[],
    selectedTagsOptions: mapTagsToOption(initialAssignedTags),
  });

  const {
    tags: tagsData,
    isFetched,
    hasNextPage,
    fetchNextPage,
  } = useInfinityTagsQuery(debouncedSearchQuery, {
    minTime: 250,
  });

  const createdSearchedOptions = useMemo(() => {
    return state.createdTags.filter((tag) =>
      tag.name.toLowerCase().includes(searchTagsQuery.toLowerCase()),
    );
  }, [state.createdTags, searchTagsQuery]);

  const options = useMemo(
    () => [
      ...createdSearchedOptions.map((tag) => ({ ...tag, group: t('common.newlyCreated') })),
      ...mapTagsToOption(tagsData).map((tag) => ({ ...tag, group: t('common.existingTags') })),
    ],
    [createdSearchedOptions, tagsData, searchTagsQuery],
  );

  const create = useCallback(
    (tagName: string) => {
      dispatch({ type: ACTION_TYPES.CREATE, payload: tagName });
    },
    [dispatch],
  );

  const update = useCallback(
    (options: Option[]) => {
      dispatch({
        type: ACTION_TYPES.UPDATE,
        payload: options.map((option) => ({ ...option, id: String(option.id) })),
      });
    },
    [dispatch],
  );

  const reset = useCallback(() => {
    dispatch({ type: ACTION_TYPES.RESET });
  }, [dispatch]);

  const search = useCallback(
    (searchQuery: string) => {
      setSearchTagsQuery(searchQuery);
    },
    [setSearchTagsQuery],
  );

  const errors = useMemo(() => {
    const errors: string[] = [];
    if (!validateTagLength(searchTagsQuery)) {
      errors.push(t('tag.signLimits'));
    }

    if (!validateNumberOfAssignedTags(state.selectedTagsOptions)) {
      errors.push(t('tag.tagsLimit'));
    }
    return errors;
  }, [searchTagsQuery, state]);

  return {
    options,
    createdOptions: createdSearchedOptions,
    selectedTagsOptions: state.selectedTagsOptions,
    isSearching: !isFetched || debouncedSearchQuery !== searchTagsQuery,
    changed: state.dirty,
    create,
    update,
    search,
    reset,
    errors,
    hasNextPage,
    fetchNextPage,
  };
};
